import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import { AlertType, type Client, type DocumentRejectionReason } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

import { resolveExclusionCascade, type ExclusionIndexScope } from "../utils/exclusionCascade";
import type { AcknowledgementInput } from "./useReviewAcknowledgements";

/**
 * True when an accept/reject failure is the read-only partner write guard
 * (ADR-035): a 400 whose JSON body carries `code: "PARTNER_READ_ONLY"`. The
 * guard fires when the active partner is an Aegle-internal one (AEGL/ACAP) or
 * the X-Partner-ID header is missing — neither can receive production writes.
 */
const isPartnerReadOnlyError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { status, body } = error as { status?: number; body?: unknown };
  if (status !== 400 || !body || typeof body !== "object") return false;
  return (body as { code?: unknown }).code === "PARTNER_READ_ONLY";
};

/** What the reject dialog hands back (#7441): a required code and optional note. */
export interface RejectDocumentInput {
  reasonCode: DocumentRejectionReason;
  note?: string;
}

interface UseReviewApprovalProps {
  documentId: string | undefined;
  /**
   * The transformation whose record indices the exclusions refer to. Needed to
   * resolve the exclusion cascade server-side at approve time (#6848).
   */
  transformationId?: string | undefined;
  selectedClient: Client | null;
  isClientMissing: boolean;
  originalClientId?: string;
  /** Indices of extracted records the user has excluded from the review (0-based). */
  excludedRecordIndices?: number[];
  /** Reviewer-confirmed reported as-of (paid-through) date (YYYY-MM-DD); drives claims authority (ADR-051). */
  reportedAsOf?: string;
  /**
   * Checks the reviewer acknowledged in the rail (#7440 AC6). Sent as
   * acknowledgements[{check, detail}]; the server stamps user and time.
   */
  acknowledgements?: AcknowledgementInput[];
  /**
   * Where this caller's exclusion indices come from, which decides whether the
   * cascade needs server-side resolution (#6848).
   *
   * REQUIRED, and a union rather than optional numbers. Both of those are the
   * result of getting it wrong: it began as three optional counts defaulting
   * to 0/0/false, which `needsServerCascade` reads as "total unknown, ask the
   * server". Right for a caller mid-load, wrong for MultiClientReviewPanel,
   * which derives its indices from a whole-document projection and never
   * calls the records endpoint — so it silently opted into a resolution it
   * does not need and every BOB approval with an exclusion was blocked.
   *
   * Three tests caught it, which is the good kind of failure. But the defect
   * was reachable because the type let a caller omit a decision it did not
   * know it was making.
   */
  exclusionScope: ExclusionIndexScope;
}

/**
 * Hook to handle document review approval and rejection
 *
 * Handles:
 * - Approving documents with optional client assignment
 * - Rejecting documents
 * - Loading states during operations
 * - Success/error notifications
 * - Navigation after completion
 *
 * @param props - Configuration for approval logic
 * @returns Approval handlers, rejection handlers, and loading states
 */
export const useReviewApproval = ({
  documentId,
  transformationId,
  selectedClient,
  isClientMissing,
  originalClientId,
  excludedRecordIndices = [],
  reportedAsOf,
  acknowledgements = [],
  exclusionScope,
}: UseReviewApprovalProps) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  /**
   * Handle document approval
   * Validates client requirement and sends approval to backend
   */
  const handleApprove = useCallback(async () => {
    if (!documentId) return;

    if (isClientMissing) {
      notify("Please select a client before approving", { type: AlertType.WARNING });
      return;
    }

    setIsApproving(true);

    try {
      // Resolve the exclusion cascade against the WHOLE record set before
      // posting (#6848). The interactive cascade only saw loaded records, so
      // on a paged document a linked claim line further in would otherwise
      // load against a claimant the reviewer had removed.
      //
      // This BLOCKS on failure rather than falling back to the loaded-only
      // set. Falling back would reproduce the exact defect with a code path
      // that looked like it had handled it.
      const cascade = await resolveExclusionCascade({
        documentId,
        transformationId,
        excludedIndices: excludedRecordIndices,
        scope: exclusionScope,
      });
      if (cascade.status === "blocked") {
        notify(cascade.message, { type: AlertType.WARNING });
        setIsApproving(false);
        return;
      }
      const resolvedExclusions = cascade.indices;

      await dataProvider.create(`documents/${documentId}/accept`, {
        data: {
          // Always send client_id_ref - either the selected client or the original from the document record
          client_id_ref: selectedClient?.id || originalClientId,
          // Indices of records the reviewer chose to exclude (backend will skip these)
          ...(resolvedExclusions.length > 0 && { excluded_record_indices: resolvedExclusions }),
          // Reviewer-confirmed paid-through (as-of) date — empty string omits it (ADR-051).
          ...(reportedAsOf && { reported_as_of: reportedAsOf }),
          // Acknowledged rail checks travel with the approval (#7440 AC6).
          ...(acknowledgements.length > 0 && { acknowledgements }),
        },
      });
      notify("Document approved successfully", { type: AlertType.SUCCESS });
      refresh();
      await navigate(`/documents/${documentId}/show`);
    } catch (error) {
      if (isPartnerReadOnlyError(error)) {
        notify("This partner is read-only — switch to a writable client partner before approving.", {
          type: AlertType.WARNING,
        });
      } else {
        notifyError(error);
      }
    } finally {
      setIsApproving(false);
    }
  }, [
    documentId,
    transformationId,
    isClientMissing,
    selectedClient,
    originalClientId,
    excludedRecordIndices,
    reportedAsOf,
    acknowledgements,
    exclusionScope,
    dataProvider,
    notify,
    notifyError,
    refresh,
    navigate,
  ]);

  /**
   * Handle document rejection (#7441).
   *
   * The reason code is REQUIRED — the server returns 400 without one and the
   * status does not change. The dialog (RejectDocumentDialog) is the only
   * caller and does not enable its confirm button until a reason is chosen,
   * so this guard is belt-and-braces: it keeps a programmatic caller from
   * reproducing the pre-#7441 "reason not provided" rows.
   */
  const handleReject = useCallback(
    async ({ reasonCode, note }: RejectDocumentInput): Promise<boolean> => {
      if (!documentId) return false;
      if (!reasonCode) {
        notify("Choose a reason to reject", { type: AlertType.WARNING });
        return false;
      }

      setIsRejecting(true);

      try {
        const trimmedNote = note?.trim() ?? "";
        await dataProvider.create(`documents/${documentId}/reject`, {
          data: {
            reason_code: reasonCode,
            // Blank note is omitted; the server stores empty either way.
            ...(trimmedNote && { note: trimmedNote }),
          },
        });
        notify("Document rejected", { type: AlertType.INFO });
        refresh();
        await navigate(`/documents/${documentId}/show`);
        return true;
      } catch (error) {
        // Returns false so the dialog stays open with the reason and note
        // intact — a 400 or PARTNER_READ_ONLY must not eat the reviewer's text.
        notifyError(error);
        return false;
      } finally {
        setIsRejecting(false);
      }
    },
    [documentId, dataProvider, notify, notifyError, refresh, navigate]
  );

  return {
    handleApprove,
    handleReject,
    isApproving,
    isRejecting,
  };
};
